// ── <SettingsModal> — Settings centered-modal popup ──
//
// Extracted verbatim from AppInner's render (App.jsx ~line 7180) that rendered
// the full Settings modal: theme pills, engine pills, tour replay, dual-engine
// comparison (with dropdowns), pipeline-debug, mock-today, forecast-tracking,
// force-resolution, replay-tos, voice/auto-read, LLM-prompt box, conditional-
// probabilities table, GCP-server test, and the HOTKEYS column.
//
// This is a behavior-preserving extraction: ALL logic is ported unchanged from
// App.jsx; only the closure values are re-sourced from props (`ctx`).
//
// The component is GATED — the caller (App.jsx) is responsible for rendering
// this component only when `proOpen` is true (preserving the exact gating and
// JSX position/z-order from AppInner).
//
// Module-level deps imported directly (same file used `import … from` in App.jsx):
//   C                    — lib/colors.js
//   FORECASTER_INFO      — lib/forecast/engines.js
//   HIDDEN_TIERS        — lib/forecast/engines.js
//   applyForecasters     — lib/forecast/engines.js
//   isPrimaryEngine      — lib/forecast/engines.js
//   engineTag            — lib/forecast/engines.js
//   tagOwner             — lib/forecast/engines.js
//   forecasterDisplayName — lib/forecast/engines.js
//   engineLabel          — lib/forecast/engines.js
//   sortEngines          — lib/forecast/engines.js
//   comparisonCleared    — lib/forecast/reducer.js
//   DrawerHelp           — components/Panels.jsx
//   HelpReopenButton     — components/Panels.jsx
//   brandProduct         — branding.js
//
// Runtime values come from `ctx` (AppInner-scoped at render time):
//   settingsTitleClickRef — ref for triple-click debug-UI toggle
//   debugMenuAllowed      — bool, /api/me's `debugMenuEnabled`: the triple-click may open the
//                           menu (wherever `debugUIEnabled` is, plus verified staff accounts)
//   debugUIAllowed        — bool, /api/me's `debugUIEnabled`: the debug routes exist here, so
//                           a debug control that calls one shows
//   drawerHelpState       — {settings: bool, …} drawer-help visibility
//   setDrawerHelpState    — setter for drawerHelpState
//   setProOpen            — setter to close the modal
//   orchestrators         — string[] from GCP health endpoint
//   forecastEngine       — currently selected engine name
//   setForecastEngine     — setter for forecastEngine
//   theme                 — current theme id
//   setTheme              — setter for theme
//   debugUI               — bool, debug-UI enabled (triple-click gate)
//   setDebugUI            — setter for debugUI
//   dynamicReportEnabled  — bool, /api/me's answer for the dynamic-report routes (the
//                           `chatboard-dynamic-report` flag AND the debug UI's server gate); the
//                           row's per-browser pref itself (`sooth-dynamicReport`) is read here,
//                           through features/report/useDynamicReport.js
//   compareMode           — bool, dual-engine comparison on
//   setCompareMode        — setter for compareMode
//   compareEngine         — currently selected compare engine (string|null)
//   setCompareEngine      — setter for compareEngine
//   proColors             — bool, pro-colors mode
//   debugMode             — bool, pipeline-debug overlay
//   setDebugMode          — setter for debugMode
//   mockToday             — string, datetime-local value for mock-today override
//   setMockToday          — setter for mockToday
//   forecastTracking      — bool, the FT gate: the deployment allow-bit alone. There is
//                           no user preference to AND with it; the opt-out was retired.
//   debugResolutionOverride — null | "resolved_yes"|"resolved_no"|"unresolved"
//   setDebugResolutionOverride — setter for debugResolutionOverride
//   debugForceAlertBadge  — bool, force the Forecast Log rail alert badge on (debug)
//   setDebugForceAlertBadge — setter for debugForceAlertBadge
//   responseCaptureOptIn  — bool, the per-user response-capture choice (debug; kept in the
//                           sooth_capture_choices cookie, hooks/useResponseCaptureOptIn.js)
//   setResponseCaptureOptIn — setter for responseCaptureOptIn
//   preprocessVariantAvailable — bool, the composed gate on the preprocessor-variant
//                           picker: the Debug reveal AND /api/me's `debugUIEnabled`
//                           (EXPOSE_DEBUG_UI + `chatboard-debug-ui`), so it is
//                           integration/staging-only (hooks/usePreprocessVariant.js)
//   preprocessVariantId   — string, the selected variant id ("" = deployment default)
//   setPreprocessVariantId — setter for preprocessVariantId (takes an id, or "")
//   preprocessVariants    — array, the advertised strategies as served
//   preprocessVariantDeploymentDefault — string, this env's flag value ("" = registry default)
//   preprocessVariantsLoading — bool, the listing fetch is in flight
//   tos                   — {current: array}|null, tos block from /api/me
//   setTosPreview         — setter to open the TosGate preview overlay
//   voiceMode             — bool, voice mode enabled
//   autoRead              — bool, auto-read responses
//   setAutoRead           — setter for autoRead
//   llmModel              — string, LLM model override
//   setLlmModel           — setter for llmModel
//   llmPrompt             — string, LLM prompt text
//   setLlmPrompt          — setter for llmPrompt
//   llmLoading            — bool, LLM request in-flight
//   setLlmLoading         — setter for llmLoading
//   llmResponse           — null | {ok, text, model}
//   setLlmResponse        — setter for llmResponse
//   gcpLoading            — bool, GCP health request in-flight
//   setGcpLoading         — setter for gcpLoading
//   gcpResponse           — null | {ok, status, text}
//   setGcpResponse        — setter for gcpResponse
//   setOrchestrators      — setter for orchestrators (updated on GCP TEST)
//   active                — active forecast run (for conditional-probs table)
//   dispatchRun           — dispatch for comparisonCleared action

import { fetchMe } from "../../../services/accountApi.js";
import { fetchHealth, postLlm } from "../../../services/forecastServerApi.js";
import { emitSettingChange } from "../../../lib/telemetry/uiIntents.js";
import React from "react";
import { eventKey } from "../../../lib/forecast/eventIdentity.js";
import { C } from "../../../lib/colors.js";
import { AUTO_DISPLAY_SCALE, DISPLAY_SCALE_PRESETS } from "../../../lib/displayScale.js";
import {
  FORECASTER_INFO,
  HIDDEN_TIERS,
  applyForecasters,
  isPrimaryEngine,
  effectiveTier,
  tagOwner,
  forecasterDisplayName,
  engineLabel,
  sortEngines,
  mirabilisLanesAvailable,
  TIER_SHORT,
  tierDesc,
  TRIGGER_LLM_OPTIONS,
  isKnownTriggerLlm,
} from "../../../lib/forecast/engines.js";
import { comparisonCleared } from "../../../lib/forecast/reducer.js";
import { postSimulate } from "../../../lib/simulateLifecycle";
import { DrawerHelpToggle, ModalShell, CloseButton } from "../../../components/Panels.jsx";
import { ui } from "../../../lib/telemetry/instrument.js";
import { SETTINGS_HELP_TIER_IN_COMPOSER } from "../../../onboarding/drawerHelp.js";
import PreprocessVariantDebugRow from "../../../components/PreprocessVariantDebugRow.jsx";
import ResponseCaptureDebugRow from "../../../components/ResponseCaptureDebugRow.jsx";
import { product as brandProduct } from "../../../branding.js";
import GlowSpineControls from "../../../components/timeline/GlowSpineControls.jsx";
import SegmentedControl from "../../../components/SegmentedControl.jsx";
import { Button } from "../../../components/ui/Primitives.jsx";
import { useDynamicReport } from "../../report/useDynamicReport.js";

// Its own chunk, loaded on the first open: it carries its service and command, and the main
// chunk's budget (app/bundle-budget.json) has little room. An optional section, so a chunk that
// fails to load logs a warning and renders nothing rather than reaching the app's error boundary.
const ConnectedAppsSection = React.lazy(() =>
  import("./ConnectedAppsSection.jsx").catch((err) => {
    console.warn("settings.connected_apps_load_failed", err);
    return { default: () => null };
  }),
);

export default function SettingsModal({ ctx }) {
  const {
    settingsTitleClickRef,
    debugMenuAllowed = false,
    debugUIAllowed = false,
    drawerHelpState,
    setDrawerHelpState,
    setProOpen,
    orchestrators,
    forecastEngine,
    setForecastEngine,
    mirabilisLanes,
    setMirabilisLanes,
    quickDeepSelectorAllowed,
    theme,
    setTheme,
    uiScale,
    uiScaleSetting,
    setUiScale,
    debugUI,
    setDebugUI,
    glowSpine,
    setGlowSpine,
    alternateSpacesEnabled,
    alternateSpaceDefault,
    setAlternateSpaceDefault,
    dynamicReportEnabled = false,
    glowControls,
    setGlowControls,
    compareMode,
    setCompareMode,
    compareEngine,
    setCompareEngine,
    triggerLlm,
    setTriggerLlm,
    compareTriggerLlm,
    setCompareTriggerLlm,
    proColors,
    debugMode,
    setDebugMode,
    mockToday,
    setMockToday,
    forecastTracking,
    debugResolutionOverride,
    debugPremiseOverride,
    setDebugPremiseOverride,
    debugForceGenerating,
    setDebugForceGenerating,
    clearDebugOverrides,
    setDebugResolutionOverride,
    debugForceAlertBadge,
    setDebugForceAlertBadge,
    responseCaptureOptIn,
    setResponseCaptureOptIn,
    preprocessVariantAvailable,
    preprocessVariantId,
    setPreprocessVariantId,
    preprocessVariants,
    preprocessVariantDeploymentDefault,
    preprocessVariantsLoading,
    tos,
    setTosPreview,
    voiceMode,
    autoRead,
    setAutoRead,
    llmModel,
    setLlmModel,
    llmPrompt,
    setLlmPrompt,
    llmLoading,
    setLlmLoading,
    llmResponse,
    setLlmResponse,
    gcpLoading,
    setGcpLoading,
    gcpResponse,
    setGcpResponse,
    setOrchestrators,
    active,
    dispatchRun,
    resetTips,
  } = ctx;
  // Settings → Debug → Dynamic reports: the per-browser pref the report modal reads too.
  const { dynamicReportPref: dynamicReport, setDynamicReportPref: setDynamicReport } =
    useDynamicReport({ debugUI, dynamicReportEnabled });
  // A user's pick of a setting is a `settings.change` (its name, never the value).
  // A pick of the value already set changes nothing and is not counted.
  const pickEngine = (v) => {
    if (v !== forecastEngine) emitSettingChange("forecaster", { origin: "settings.forecaster" });
    setForecastEngine(v);
  };
  const pickTheme = (v) => {
    if (v !== theme) emitSettingChange("theme", { origin: "settings.theme" });
    setTheme(v);
  };
  const pickSize = (v) => {
    if (v !== uiScaleSetting) {
      emitSettingChange("display_size", { origin: "settings.display-size" });
    }
    setUiScale(v);
  };

  // "Simulate lifecycle" debug buttons: fire a REAL reforecast/resolution for the
  // active forecast against the dev/integration-only route. `simMsg` is inline
  // feedback (this member has no toast); `_simId` is the wire client_id used in
  // the forecast-scoped URL (`active.forecastId || active.id`, per App.jsx).
  const [simMsg, setSimMsg] = React.useState(null);
  // The route keys on forecasts.client_id (like /resolve + PATCH). In the run
  // store that's `active.id` (run.runId = wire `id` = client_id); `active.forecastId`
  // is the pg UUID, which is NOT what this route matches. Prefer id; fall back to
  // forecastId (the route also tolerates the UUID form).
  const _simId = active?.id || active?.forecastId || null;
  const runSim = async (action, verdict) => {
    if (!_simId) {
      setSimMsg("No active forecast to simulate.");
      return;
    }
    const isReforecast = action === "reforecast" || action === "reforecast_threshold";
    setSimMsg(isReforecast ? "Starting real reforecast (LLM)…" : "Resolving…");
    let r;
    try {
      r = await postSimulate(_simId, action, verdict);
    } catch (e) {
      // A network reject would otherwise leave the modal stuck on the optimistic
      // "Starting…" message (and surface as an unhandled rejection).
      setSimMsg(`Failed: ${(e && e.message) || "network error"}`);
      return;
    }
    if (!r.ok) {
      setSimMsg(`Failed (${r.status}): ${r.body?.error || "error"}`);
      return;
    }
    if (isReforecast) {
      // Fire-and-forget: the server runs the real forecaster in the background
      // (a real LLM run + a slow emulator BigTable write) and returns 202
      // immediately, so a browser refresh can't cancel it. The new point + report
      // land in ~1 min — refresh to see them.
      setSimMsg("Reforecast running in the background — refresh in ~1 min to see the new point.");
    } else {
      const real = r.body?.real;
      setSimMsg(
        `Resolved ${real?.status || verdict} — resolution report written (BigQuery). Watchlist email → Mailpit.`,
      );
    }
  };

  // Seed the "Set today's date & time" picker with the CURRENT local instant so
  // it opens on a sensible starting point the user can edit — WITHOUT freezing
  // the session clock. `mockToday` state stays empty (opt-in: effectiveToday uses
  // live wall-clock while empty — see the App.jsx decl comment). This is used as
  // the input's `defaultValue` (UNCONTROLLED): it seeds the field on each open
  // (the modal remounts when re-opened) but lets the user clear it back to blank
  // without the value snapping back, and avoids cursor jumps from re-computing
  // `nowLocal` on every render. onChange writes the pick to state and activates
  // the override. datetime-local wants "YYYY-MM-DDTHH:MM" in LOCAL time (not
  // UTC), so format from the local getters rather than toISOString.
  const _pad = (n) => String(n).padStart(2, "0");
  const _now = new Date();
  const nowLocal = `${_now.getFullYear()}-${_pad(_now.getMonth() + 1)}-${_pad(_now.getDate())}T${_pad(_now.getHours())}:${_pad(_now.getMinutes())}`;

  // Shared row layout for Forecaster + Theme — title to the LEFT of the control.
  const rowStyle = { display: "flex", alignItems: "center", gap: 16 };
  // Fixed (not min) width so the label column is identical across rows —
  // "Forecaster" is wider than the old 74px floor, which pushed its control
  // right of the shorter "Theme"/"Tour" rows. A fixed width lines every
  // control (and the Tour button, which uses the same 96px width) up flush.
  const titleStyle = {
    fontSize: 12,
    color: C.white,
    letterSpacing: 0.6,
    fontWeight: "bold",
    width: 96,
    flexShrink: 0,
  };
  // Shared geometry so the Forecaster and Theme controls line up exactly:
  // same total width (CONTROL_W) and same per-cell height. Each control is
  // a fixed-width flex container whose cells are `flex:1 1 0` — so the two
  // Forecaster segments and the four Theme buttons span the identical
  // left-to-right extent, each cell within a control equal width, and every
  // cell the same height (minHeight on the shared cell base).
  const CONTROL_W = 504;
  // maxWidth:100% caps the fixed width on narrow viewports (mobile) so the
  // control shrinks with the modal instead of overflowing / forcing scroll.
  const controlWrap = {
    display: "flex",
    width: CONTROL_W,
    maxWidth: "100%",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    overflow: "hidden",
  };
  const cellBase = {
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "3px 10px",
    minHeight: 40,
    cursor: "pointer",
    userSelect: "none",
    transition: "background 0.15s, color 0.15s",
    fontFamily: '"IBM Plex Mono",monospace',
  };
  // Roving-tabindex keyboard nav for the role="radio" segmented controls:
  // only the selected cell is in the tab order (tabIndex 0; the rest -1),
  // arrows move focus to the adjacent cell AND select it (selection follows
  // focus, per the ARIA radiogroup pattern), Home/End jump to the ends, and
  // Enter/Space selects the focused cell. The sibling is focused straight off
  // the container's children (same order as the map), so no refs are needed.
  const onRovingKey = (e, count, curIdx, pick) => {
    const k = e.key;
    let ni = -1;
    if (k === "ArrowRight" || k === "ArrowDown") ni = (curIdx + 1) % count;
    else if (k === "ArrowLeft" || k === "ArrowUp") ni = (curIdx - 1 + count) % count;
    else if (k === "Home") ni = 0;
    else if (k === "End") ni = count - 1;
    else if (k === "Enter" || k === " ") ni = curIdx;
    else return;
    e.preventDefault();
    pick(ni);
    e.currentTarget.parentElement?.children[ni]?.focus();
  };
  // Engine-set segments, ordered default-first so Mirabilis is the left cell —
  // the same reading order as every other control here.
  const ENGINE_SET_OPTS = [
    {
      id: "mirabilis",
      mirabilis: true,
      label: "Mirabilis",
      desc: "The newer Mirabilis lanes, where deployed",
    },
    {
      id: "standard",
      mirabilis: false,
      label: "Standard",
      desc: "The Quick and Deep forecasters from before Mirabilis",
    },
  ];

  // The keyboard shortcuts, named the way the app names things.
  const SHORTCUTS = [
    [["Shift", "↑"], "New Question"],
    [["Shift", "↓"], "Signals"],
    [["Shift", "←"], "Forecasts"],
    [["Shift", "→"], "Chat"],
    [["+"], "Zoom in"],
    [["−"], "Zoom out"],
    [["←", "→"], "Pan the timeline"],
    [["Shift", "drag"], "Zoom to a range"],
    [["Double-click the timeline"], "Add a trigger"],
  ];
  const kbd = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 22,
    height: 22,
    padding: "0 6px",
    boxSizing: "border-box",
    border: `1px solid ${C.border}`,
    borderBottomWidth: 2,
    borderRadius: 6,
    background: C.rowInactive,
    color: C.grayL,
    fontSize: 12,
    lineHeight: 1,
    whiteSpace: "nowrap",
  };
  const sectionTitle = {
    fontSize: 13,
    fontWeight: 600,
    color: C.white,
    letterSpacing: 0,
    margin: "0 0 4px",
  };
  // One labelled row of a section: the setting's name on the left, its control on the right.
  const row = (label, control, hint) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "120px minmax(0, 1fr)",
        alignItems: "center",
        gap: 16,
        padding: "12px 0",
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <div>
        <div style={{ fontSize: 13, color: C.grayL }}>{label}</div>
        {hint ? <div style={{ fontSize: 12, color: C.gray, marginTop: 2 }}>{hint}</div> : null}
      </div>
      <div style={{ minWidth: 0 }}>{control}</div>
    </div>
  );
  const sorted = sortEngines(orchestrators || []);
  const primaryEngines = sorted.filter((e) => isPrimaryEngine(e));
  const mainEngines = primaryEngines.length ? primaryEngines : sorted;
  const depthOptions = mainEngines.map((e) => {
    const tag = effectiveTier(e);
    return {
      value: e,
      label: TIER_SHORT[tag] || engineLabel(e),
      hint: tierDesc(tag) || undefined,
      ui: ui("settings.forecaster", e),
    };
  });
  const autoLabel = DISPLAY_SCALE_PRESETS.find((p) => p.scale === uiScale)?.label;
  const SIZE_OPTS = [
    {
      value: AUTO_DISPLAY_SCALE,
      label: uiScaleSetting === AUTO_DISPLAY_SCALE && autoLabel ? `Auto · ${autoLabel}` : "Auto",
      ui: ui("settings.display-size", String(AUTO_DISPLAY_SCALE)),
    },
    ...DISPLAY_SCALE_PRESETS.map((p) => ({
      value: p.scale,
      label: p.label,
      ui: ui("settings.display-size", String(p.scale)),
    })),
  ];
  const THEME_OPTS = [
    { value: "dev", label: "Dark", ui: ui("settings.theme", "dev") },
    { value: "dusk", label: "Dawn", ui: ui("settings.theme", "dusk") },
    { value: "base", label: "Bichromatic", ui: ui("settings.theme", "base") },
  ];

  return (
    <ModalShell
      onClose={() => setProOpen(false)}
      surface="settings"
      zIndex={550}
      panelProps={{ "data-settings-panel": "", "data-telemetry-redact": "" }}
      panelStyle={{
        boxShadow: C.shadowBox,
        width: debugUI ? "min(1060px, 95vw)" : "min(620px, 94vw)",
        maxHeight: "85vh",
        overflowY: "auto",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        marginTop: "6vh",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", padding: "20px 24px 8px" }}>
        <h2
          {...ui("settings.title")}
          // Triple-click opens the debug menu, but only where the server allows it
          // (`debugMenuEnabled` from /api/me); everywhere else the title is just a title.
          onClick={() => {
            if (!debugMenuAllowed) return;
            const now = Date.now();
            const ref = settingsTitleClickRef.current;
            ref.count = now - ref.t < 700 ? ref.count + 1 : 1;
            ref.t = now;
            if (ref.count >= 3) {
              setDebugUI((v) => !v);
              ref.count = 0;
            }
          }}
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 600,
            color: C.white,
            letterSpacing: 0,
            cursor: "default",
            userSelect: "none",
          }}
        >
          Settings
        </h2>
        <div style={{ flex: 1 }} />
        <CloseButton
          dataPopupCancel
          onClick={() => setProOpen(false)}
          style={{ width: 32, height: 32, borderRadius: 8, fontSize: 18, color: C.gray }}
        />
      </div>
      <DrawerHelpToggle
        k="settings"
        corner="br"
        // The default copy mentions choosing a depth HERE; with the composer selector live
        // that control is no longer on this panel.
        bodyOverride={quickDeepSelectorAllowed ? SETTINGS_HELP_TIER_IN_COMPOSER : undefined}
        drawerHelpState={drawerHelpState}
        setDrawerHelpState={setDrawerHelpState}
        boxStyle={{ margin: "4px 24px 8px" }}
      />
      <div style={{ padding: "8px 24px 28px", display: "flex", flexDirection: "column", gap: 24 }}>
        <section>
          <h3 style={sectionTitle}>Appearance</h3>
          {row(
            "Theme",
            <SegmentedControl
              label="Theme"
              options={THEME_OPTS}
              value={theme}
              onChange={pickTheme}
              block
            />,
          )}
          {row(
            "Display size",
            <SegmentedControl
              label="Display size"
              options={SIZE_OPTS}
              value={uiScaleSetting}
              onChange={pickSize}
              block
            />,
          )}
        </section>
        {/* Where the composer's Quick/Deep selector is live it is the one way to pick a depth;
            elsewhere this row is the only one, so it stays. */}
        {!quickDeepSelectorAllowed && (
          <section>
            <h3 style={sectionTitle}>Forecasting</h3>
            {row(
              "Depth",
              <SegmentedControl
                label="Depth"
                options={depthOptions}
                value={forecastEngine}
                onChange={pickEngine}
                block
              />,
            )}
          </section>
        )}
        <section>
          <h3 style={sectionTitle}>Getting started</h3>
          {row(
            "Tips",
            <Button
              size="sm"
              {...ui("settings.show-tips")}
              onClick={() => {
                setProOpen(false);
                resetTips();
              }}
            >
              Show tips again
            </Button>,
            "Short notes beside each feature the first time you see it",
          )}
        </section>
        <React.Suspense fallback={null}>
          <ConnectedAppsSection />
        </React.Suspense>
        <section>
          <h3 style={sectionTitle}>Keyboard shortcuts</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              columnGap: 24,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            {SHORTCUTS.map(([keys, action]) => (
              <div
                key={action}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "9px 0",
                  borderBottom: `1px solid ${C.border}`,
                  gridColumn: keys[0].length > 8 ? "1 / -1" : undefined,
                }}
              >
                <span style={{ fontSize: 13, color: C.white }}>{action}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {keys.map((k, i) =>
                    k.length > 8 ? (
                      <span key={i} style={{ fontSize: 12, color: C.gray }}>
                        {k}
                      </span>
                    ) : (
                      <kbd key={i} className="sy-mono" style={kbd}>
                        {k}
                      </kbd>
                    ),
                  )}
                </span>
              </div>
            ))}
          </div>
        </section>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Debug-only sections below: revealed by triple-clicking the Settings title where the server allows it. */}

          {debugUI && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {/*
                Which forecasters the Quick and Deep tiers actually run. The
                `sayer-*-tier-lane` flags move the /health tag onto a Mirabilis
                lane per DEPLOYMENT and every browser follows them by default;
                this row is the per-browser opt-out back to the forecasters the
                tier had before that cutover.

                Rendered only where the toggle can do something — a Mirabilis lane
                holds a tag AND the lane it replaced is still servable. That reads
                the live registry rather than the deployment space, so it follows
                the flags as they roll out instead of restating their targeting
                where the two could silently disagree. Lives in the hidden debug
                menu (triple-click SETTINGS), not the main rows: it is an internal
                lane opt-out, not a customer choice. It picks the ENGINE behind a
                tier, not the tier, so it never competes with the composer's
                Quick/Deep control.
              */}
              {mirabilisLanesAvailable() && (
                <div style={rowStyle}>
                  <div style={titleStyle}>Engines</div>
                  <div
                    role="radiogroup"
                    aria-label="Forecaster engines"
                    style={{ ...controlWrap, alignItems: "stretch" }}
                  >
                    {ENGINE_SET_OPTS.map((opt, i) => {
                      const sel = !!mirabilisLanes === opt.mirabilis;
                      return (
                        <div
                          key={opt.id}
                          {...ui("settings.debug-engine-set", opt.id)}
                          role="radio"
                          aria-checked={sel}
                          tabIndex={sel ? 0 : -1}
                          onKeyDown={(e) =>
                            onRovingKey(e, ENGINE_SET_OPTS.length, i, (ni) =>
                              setMirabilisLanes(ENGINE_SET_OPTS[ni].mirabilis),
                            )
                          }
                          onClick={() => setMirabilisLanes(opt.mirabilis)}
                          style={{
                            ...cellBase,
                            background: sel
                              ? theme === "dusk"
                                ? C.accent
                                : C.action
                              : "transparent",
                            color: sel ? (theme === "dusk" ? "#1a1a1a" : C.bg) : C.white,
                            borderLeft: i > 0 ? `1px solid ${C.border}` : "none",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: "bold",
                              letterSpacing: 0,
                              lineHeight: 1.3,
                            }}
                          >
                            {opt.label}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              marginTop: 3,
                              lineHeight: 1.35,
                              whiteSpace: "nowrap",
                              color: sel ? C.bg : C.gray,
                              opacity: sel ? 0.8 : 1,
                            }}
                          >
                            {opt.desc}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <label className="settings-toggle-row">
                <div
                  {...ui("settings.debug-compare-mode")}
                  onClick={() =>
                    setCompareMode((v) => {
                      const next = !v;
                      // Auto-pick a sensible second forecaster the first time Dual-Engine
                      // is enabled, and clear the selection when it's turned off.
                      if (next) {
                        if (!compareEngine || compareEngine === forecastEngine) {
                          // Pairing default: the OTHER tier default (quick↔deep via tags),
                          // else the first supported forecaster that isn't the primary.
                          // Compare may use ANY supported forecaster — the candidate pool
                          // is the full registry, not the customer-visible list.
                          const all = Object.keys(FORECASTER_INFO);
                          const otherTag =
                            effectiveTier(forecastEngine) === "quick" ? "deep" : "quick";
                          const flipped = tagOwner(otherTag);
                          const pick =
                            flipped && flipped !== forecastEngine && all.includes(flipped)
                              ? flipped
                              : all.find((e) => e !== forecastEngine) || null;
                          setCompareEngine(pick);
                        }
                      } else {
                        // Turning Dual-Engine OFF: clear BOTH the secondary engine AND the
                        // comparison selection. Without clearing comparison, rootIds (and
                        // thus compareRoots[1]) survive, so the URL keeps a stale ?compare=
                        // and the compareRoots[1]-gated surfaces (contract drawer 2-col,
                        // debug panel) keep showing compare UI even though the box is off.
                        setCompareEngine(null);
                        dispatchRun(comparisonCleared()); // dual-write comparison clear on Dual-Engine OFF
                      }
                      return next;
                    })
                  }
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    border: `2px solid ${compareMode ? (proColors ? C.purple : C.green) : C.border}`,
                    background: compareMode ? (proColors ? C.purple : C.green) : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                >
                  {compareMode && <span className="settings-toggle-check">✓</span>}
                </div>
                <div>
                  <div className="settings-toggle-label">Dual-Engine Comparison</div>
                  <div className="settings-hint">Run two engines in parallel</div>
                </div>
              </label>
              {/* Dual-Engine forecaster pickers — debug mode is the ONE surface that
          shows server display names. Two dropdowns over ALL supported
          forecasters (the whole registry, hidden tiers included);
          both dropdowns list all forecasters, and the same forecaster MAY
          occupy both slots (intentional — to compare different trigger
          LLMs on one forecaster). */}
              {compareMode &&
                (() => {
                  const all = Object.keys(FORECASTER_INFO);
                  const selStyle = {
                    background: C.inputBg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.white,
                    fontFamily: '"IBM Plex Mono",monospace',
                    fontSize: 11,
                    padding: "3px 6px",
                    outline: "none",
                    cursor: "pointer",
                    maxWidth: 220,
                  };
                  const renderOptions = () =>
                    all.map((e) => (
                      <option key={e} value={e}>
                        {forecasterDisplayName(e)}
                      </option>
                    ));
                  // Trigger-LLM picker for one leg. `value`/`setValue` are the leg's
                  // triggerLlm state, which is exactly one of three things:
                  //   • null            → server default (no override)
                  //   • a non-empty id  → a concrete model (known or custom)
                  //   • "__custom__"    → custom mode selected, no id typed yet
                  // The "__custom__" sentinel (never a bare "") is what renders the
                  // free-text box while the box is empty; it is treated as "no override"
                  // everywhere it is consumed — never persisted (useEngineSelection),
                  // never sent on the wire (forecastClient omits it), never displayed
                  // (the debug panel reads the server-echoed model, not this state).
                  const triggerPicker = (label, color, value, setValue, testid) => {
                    const selectValue =
                      value == null ? "" : isKnownTriggerLlm(value) ? value : "__custom__";
                    const isCustom = selectValue === "__custom__";
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div
                            style={{
                              fontSize: 11,
                              color,
                              fontWeight: "bold",
                              letterSpacing: 0,
                              minWidth: 74,
                            }}
                          >
                            {label}
                          </div>
                          <select
                            data-testid={testid}
                            {...ui("settings.debug-trigger-llm", testid)}
                            aria-label={`${label} trigger LLM`}
                            value={selectValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              setValue(
                                v === ""
                                  ? null
                                  : v === "__custom__"
                                    ? isKnownTriggerLlm(value) || value == null
                                      ? "__custom__"
                                      : value
                                    : v,
                              );
                            }}
                            style={{ ...selStyle, borderColor: color }}
                          >
                            {TRIGGER_LLM_OPTIONS.map((o) => (
                              <option
                                key={o.id == null ? "__default__" : o.id}
                                value={o.id == null ? "" : o.id}
                              >
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {isCustom && (
                          <input
                            data-testid={`${testid}-custom`}
                            {...ui("settings.debug-trigger-llm-custom", testid)}
                            aria-label={`${label} custom trigger LLM id`}
                            type="text"
                            placeholder="vendor/model (e.g. openai/gpt-5.5)"
                            value={value === "__custom__" ? "" : value || ""}
                            onChange={(e) => setValue(e.target.value.trim() || "__custom__")}
                            style={{ ...selStyle, marginLeft: 82, maxWidth: 220, cursor: "text" }}
                          />
                        )}
                      </div>
                    );
                  };
                  return (
                    <div
                      style={{ marginLeft: 24, display: "flex", flexDirection: "column", gap: 6 }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#aa88ff",
                            fontWeight: "bold",
                            letterSpacing: 0,
                            minWidth: 74,
                          }}
                        >
                          ENGINE A
                        </div>
                        <select
                          data-testid="compare-engine-a"
                          {...ui("settings.debug-compare-engine", "a")}
                          aria-label="Engine A"
                          value={all.includes(forecastEngine) ? forecastEngine : ""}
                          onChange={(e) => setForecastEngine(e.target.value)}
                          style={{ ...selStyle, borderColor: "#aa88ff" }}
                        >
                          {!all.includes(forecastEngine) && (
                            <option value="" disabled>
                              —
                            </option>
                          )}
                          {renderOptions()}
                        </select>
                      </div>
                      {triggerPicker(
                        "TRIGGER A",
                        "#aa88ff",
                        triggerLlm,
                        setTriggerLlm,
                        "trigger-llm-a",
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#33aa88",
                            fontWeight: "bold",
                            letterSpacing: 0,
                            minWidth: 74,
                          }}
                        >
                          ENGINE B
                        </div>
                        <select
                          data-testid="compare-engine-b"
                          {...ui("settings.debug-compare-engine", "b")}
                          aria-label="Engine B"
                          value={compareEngine && all.includes(compareEngine) ? compareEngine : ""}
                          onChange={(e) => setCompareEngine(e.target.value)}
                          style={{ ...selStyle, borderColor: "#33aa88" }}
                        >
                          {(!compareEngine || !all.includes(compareEngine)) && (
                            <option value="" disabled>
                              —
                            </option>
                          )}
                          {renderOptions()}
                        </select>
                      </div>
                      {triggerPicker(
                        "TRIGGER B",
                        "#33aa88",
                        compareTriggerLlm,
                        setCompareTriggerLlm,
                        "trigger-llm-b",
                      )}
                    </div>
                  );
                })()}
              {/* Glow spine (experimental) — swaps the dashed timeline spine for
                  the SVG GlowSpine. Off by default; the 5 tuning sliders appear
                  only when it's on. */}
              <label className="settings-toggle-row">
                <div
                  {...ui("settings.debug-glow-spine")}
                  onClick={() => setGlowSpine((v) => !v)}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    border: `2px solid ${glowSpine ? (proColors ? C.purple : C.green) : C.border}`,
                    background: glowSpine ? (proColors ? C.purple : C.green) : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                >
                  {glowSpine && <span className="settings-toggle-check">✓</span>}
                </div>
                <div>
                  <div className="settings-toggle-label">Glow spine (experimental)</div>
                  <div className="settings-hint">Undulating SVG timeline spine</div>
                </div>
              </label>
              {glowSpine && (
                <GlowSpineControls value={glowControls} onChange={setGlowControls} C={C} />
              )}
              {alternateSpacesEnabled && (
                <label className="settings-toggle-row">
                  <div
                    {...ui("settings.debug-alternate-space")}
                    role="checkbox"
                    aria-checked={!!alternateSpaceDefault}
                    aria-label="Alternate space by default"
                    tabIndex={0}
                    onClick={() => setAlternateSpaceDefault((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        setAlternateSpaceDefault((v) => !v);
                      }
                    }}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 3,
                      border: `2px solid ${alternateSpaceDefault ? (proColors ? C.purple : C.green) : C.border}`,
                      background: alternateSpaceDefault
                        ? proColors
                          ? C.purple
                          : C.green
                        : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    {alternateSpaceDefault && <span className="settings-toggle-check">✓</span>}
                  </div>
                  <div>
                    <div className="settings-toggle-label">Alternate space by default</div>
                    <div className="settings-hint">
                      Show the alternate outcome space when a forecast has one
                    </div>
                  </div>
                </label>
              )}
              {dynamicReportEnabled && (
                <label className="settings-toggle-row">
                  <div
                    {...ui("settings.debug-dynamic-report")}
                    role="checkbox"
                    aria-checked={!!dynamicReport}
                    aria-label="Dynamic reports"
                    tabIndex={0}
                    onClick={() => setDynamicReport((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        setDynamicReport((v) => !v);
                      }
                    }}
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 3,
                      border: `2px solid ${dynamicReport ? (proColors ? C.purple : C.green) : C.border}`,
                      background: dynamicReport ? (proColors ? C.purple : C.green) : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    {dynamicReport && <span className="settings-toggle-check">✓</span>}
                  </div>
                  <div>
                    <div className="settings-toggle-label">Dynamic reports</div>
                    <div className="settings-hint">
                      Offer an on-demand, agent-written explainer in the report view.
                    </div>
                  </div>
                </label>
              )}
              <label className="settings-toggle-row">
                <div
                  {...ui("settings.debug-pipeline")}
                  onClick={() => setDebugMode((v) => !v)}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    border: `2px solid ${debugMode ? (proColors ? C.purple : "#33aa88") : C.border}`,
                    background: debugMode ? (proColors ? C.purple : "#33aa88") : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                >
                  {debugMode && <span className="settings-toggle-check">✓</span>}
                </div>
                <div>
                  <div className="settings-toggle-label">Pipeline Debug</div>
                  <div className="settings-hint">Show postprocess pipeline inputs</div>
                </div>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <div className="settings-toggle-label">Set today&rsquo;s date &amp; time</div>
                  <div className="settings-hint">
                    Override the instant {brandProduct.prose} treats as TODAY
                  </div>
                </div>
                <input
                  {...ui("settings.debug-mock-today")}
                  type="datetime-local"
                  defaultValue={mockToday || nowLocal}
                  onChange={(e) => setMockToday(e.target.value)}
                  style={{
                    background: C.inputBg,
                    border: `1px solid ${C.purple}`,
                    borderRadius: 6,
                    color: C.white,
                    fontFamily: '"IBM Plex Mono",monospace',
                    fontSize: 11,
                    padding: "3px 6px",
                    outline: "none",
                    cursor: "pointer",
                  }}
                />
              </div>
              {/* Force Alert Badge — debug-only. Forces the Forecast Log rail's alert
          badge (bell + count) to show with a representative count, regardless
          of real f.alert flags or the Forecast Tracking gate, so the badge can
          be styled/worked on without waiting for a threshold-crossing
          reforecast. Purely visual — mutates no forecast record. */}
              <label className="settings-toggle-row">
                <div
                  {...ui("settings.debug-force-alert-badge")}
                  onClick={() => setDebugForceAlertBadge((v) => !v)}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    border: `2px solid ${debugForceAlertBadge ? (proColors ? C.purple : "#33aa88") : C.border}`,
                    background: debugForceAlertBadge
                      ? proColors
                        ? C.purple
                        : "#33aa88"
                      : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                    flexShrink: 0,
                  }}
                >
                  {debugForceAlertBadge && <span className="settings-toggle-check">✓</span>}
                </div>
                <div>
                  <div className="settings-toggle-label">Force Alert Badge</div>
                  <div className="settings-hint">
                    Force the Forecast Log rail alert badge to show (count = 3) for styling/work
                  </div>
                </div>
              </label>
              <ResponseCaptureDebugRow
                value={responseCaptureOptIn}
                onChange={setResponseCaptureOptIn}
                proColors={proColors}
              />
              {/* Preprocessor variant — which authoring strategy the contract server uses
          for the NEXT question submitted. Renders nothing unless this deployment's debug
          surface is available (NOT merely the Debug reveal, which is on in prod too): the
          control changes what the pipeline runs, so it is integration/staging-only. The
          selection is session-only on purpose — see hooks/usePreprocessVariant.js. */}
              <PreprocessVariantDebugRow
                available={preprocessVariantAvailable}
                value={preprocessVariantId}
                onChange={setPreprocessVariantId}
                variants={preprocessVariants}
                deploymentDefault={preprocessVariantDeploymentDefault}
                loading={preprocessVariantsLoading}
                proColors={proColors}
              />
              {/* Force Resolution State — debug-only override for the
          resolution-pie visual on the timeline. Lets the user
          preview each terminal verdict without waiting for the
          real sooth-resolve call to land (which takes ~120–180s)
          and without needing a past resolution date. The override
          is purely client-side / visual — it does NOT mutate
          active.resolution on the forecast record, so it can't
          accidentally persist. Gated under forecastTracking since
          the verdict overlay only renders when FT is on. */}
              {forecastTracking && (
                <div
                  style={{
                    marginLeft: 24,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div className="settings-toggle-label">Force Resolution State</div>
                    <div className="settings-hint">
                      Preview the verdict visual on the resolution pie
                    </div>
                  </div>
                  {(() => {
                    const btn = (label, value, bg) => {
                      const active = debugResolutionOverride === value;
                      return (
                        <button
                          key={value}
                          {...ui("settings.debug-resolution-override", value)}
                          type="button"
                          onClick={() => setDebugResolutionOverride(active ? null : value)}
                          style={{
                            background: active ? bg : C.btnBg,
                            color: active ? "#fff" : C.grayL,
                            border: `1px solid ${active ? bg : C.border}`,
                            borderRadius: 6,
                            padding: "3px 8px",
                            fontFamily: '"IBM Plex Mono",monospace',
                            fontSize: 11,
                            fontWeight: "bold",
                            letterSpacing: 0,
                            cursor: "pointer",
                            userSelect: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {label}
                        </button>
                      );
                    };
                    return (
                      <>
                        {btn("OCCURRED", "resolved_yes", C.blueTeam)}
                        {btn("DID NOT OCCUR", "resolved_no", C.redTeam)}
                        {btn("UNRESOLVED", "unresolved", C.grayL)}
                        {debugResolutionOverride && (
                          <button
                            {...ui("settings.debug-resolution-clear")}
                            type="button"
                            onClick={() => setDebugResolutionOverride(null)}
                            title="Clear override"
                            style={{
                              background: "transparent",
                              color: C.gray,
                              border: "none",
                              fontFamily: '"IBM Plex Mono",monospace',
                              fontSize: 11,
                              cursor: "pointer",
                              letterSpacing: 0,
                            }}
                          >
                            CLEAR
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              {/* Force Branch Premise — debug-only override for every BRANCH's premise
          state on the active lane. Threaded into premiseViewsFor, so it drives the whole
          chain at once: the fork node's fill/ring/glyph, the dashed line, whether
          downstream triggers draw, and whether the terminal panel replaces the bars.
          Purely a view-layer forcing — see useDebugOverrides.

          Why it exists: reaching these states for real needs a minted trigger contract
          AND a graded resolver verdict AND a past window, so most of the branch surface
          is otherwise unreachable during development. ALIGNED means the world agreed
          with the user's assumption (confirmed); MISALIGNED means it went the other way
          (contradicted) — the verdict is synthesized to match, so the node's check/cross
          stays consistent with the panel. */}
              {forecastTracking && (
                <div
                  style={{
                    marginLeft: 24,
                    marginTop: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div className="settings-toggle-label">Force Branch Premise</div>
                    <div className="settings-hint">
                      Preview every branch state — node, line, triggers and panel
                    </div>
                  </div>
                  {(() => {
                    const btn = (label, value, bg, title) => {
                      const on = debugPremiseOverride === value;
                      return (
                        <button
                          key={value}
                          {...ui("settings.debug-premise-override", value)}
                          type="button"
                          title={title}
                          onClick={() => setDebugPremiseOverride(on ? null : value)}
                          style={{
                            background: on ? bg : C.btnBg,
                            color: on ? "#fff" : C.grayL,
                            border: `1px solid ${on ? bg : C.border}`,
                            borderRadius: 6,
                            padding: "3px 8px",
                            fontFamily: '"IBM Plex Mono",monospace',
                            fontSize: 11,
                            fontWeight: "bold",
                            letterSpacing: 0,
                            cursor: "pointer",
                            userSelect: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {label}
                        </button>
                      );
                    };
                    return (
                      <>
                        {btn(
                          "ALIGNED",
                          "aligned",
                          C.blueTeam,
                          "Trigger resolved the way the user assumed — branch confirmed, stays live",
                        )}
                        {btn(
                          "MISALIGNED",
                          "misaligned",
                          C.redTeam,
                          "Trigger resolved the opposite way — branch contradicted, dead",
                        )}
                        {btn(
                          "PENDING",
                          "pending",
                          C.gray,
                          "Not yet in the trigger's window — nothing decided",
                        )}
                        {btn(
                          "IN WINDOW",
                          "watching",
                          C.purpleL,
                          "Inside the window, undecided — a NO would only mean 'not yet'",
                        )}
                        {btn(
                          "PAST WINDOW",
                          "overdue",
                          C.purpleL,
                          "Window closed with no ruling yet — a late verdict can still settle it",
                        )}
                        {btn(
                          "INDETERMINATE",
                          "indeterminate",
                          C.grayL,
                          "Graded but nothing established — tie or unresolved",
                        )}
                        {/* The MAIN forecast's pending state. Holds the lane in its
                            awaiting/generating layout — the "Generating Forecast" sweep and
                            the dash gap cut for its label — which is otherwise only visible
                            during a real in-flight forecast and impossible to pause. */}
                        <button
                          {...ui("settings.debug-force-generating")}
                          type="button"
                          title="Hold the main forecast in its generating/awaiting layout"
                          onClick={() => setDebugForceGenerating(!debugForceGenerating)}
                          style={{
                            background: debugForceGenerating ? C.purple : C.btnBg,
                            color: debugForceGenerating ? "#fff" : C.grayL,
                            border: `1px solid ${debugForceGenerating ? C.purple : C.border}`,
                            borderRadius: 6,
                            padding: "3px 8px",
                            fontFamily: '"IBM Plex Mono",monospace',
                            fontSize: 11,
                            fontWeight: "bold",
                            letterSpacing: 0,
                            cursor: "pointer",
                            userSelect: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          GENERATING
                        </button>
                        {(debugPremiseOverride ||
                          debugResolutionOverride ||
                          debugForceGenerating) && (
                          <button
                            {...ui("settings.debug-clear-overrides")}
                            type="button"
                            onClick={clearDebugOverrides}
                            title="Clear both debug overrides"
                            style={{
                              background: "transparent",
                              color: C.gray,
                              border: "none",
                              fontFamily: '"IBM Plex Mono",monospace',
                              fontSize: 11,
                              cursor: "pointer",
                              letterSpacing: 0,
                            }}
                          >
                            CLEAR ALL
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              {/* Simulate lifecycle — dev/integration-only: fire a REAL reforecast
          or resolution for the active forecast via handleForecastEvent (verdict
          stamp + threshold/watchlist emails + realtime frame). The route 404s
          outside local/integration, so a click there just reports failure.
          Gated under forecastTracking (same as Force Resolution above), and hidden
          where the debug routes do not exist (a staff-only menu in prod). */}
              {forecastTracking && debugUIAllowed && (
                <div
                  style={{
                    marginLeft: 24,
                    marginTop: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div className="settings-toggle-label">Simulate lifecycle</div>
                    <div className="settings-hint">
                      Fire a REAL reforecast / resolution for the active forecast (dev/integration
                      only)
                    </div>
                  </div>
                  {[
                    ["REFORECAST", "reforecast", undefined, C.grayL],
                    ["REFORECAST ↑THRESHOLD", "reforecast_threshold", undefined, C.grayL],
                    ["RESOLVE YES", "resolve", "yes", C.blueTeam],
                    ["RESOLVE NO", "resolve", "no", C.redTeam],
                  ].map(([label, action, verdict, fg]) => (
                    <button
                      key={label}
                      {...ui("settings.debug-simulate", verdict ? `${action}-${verdict}` : action)}
                      type="button"
                      onClick={() => runSim(action, verdict)}
                      style={{
                        background: C.btnBg,
                        color: fg,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        padding: "3px 8px",
                        fontFamily: '"IBM Plex Mono",monospace',
                        fontSize: 11,
                        fontWeight: "bold",
                        letterSpacing: 0,
                        cursor: "pointer",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                  {simMsg && (
                    <div
                      style={{
                        width: "100%",
                        fontSize: 11,
                        color: C.gray,
                        fontFamily: '"IBM Plex Mono",monospace',
                      }}
                    >
                      {simMsg}
                    </div>
                  )}
                </div>
              )}
              {/* Replay ToS gate — the terms-of-service window normally only shows on
          first login (when the server reports tos.isCurrent === false). This
          renders it as a dismissible PREVIEW overlay (TosGate's onPreviewClose
          mode), independent of auth/session — so it works on every deployment
          AND in local dev where there's no session and the gate is inactive.
          Doc list priority: in-memory tos.current → /api/me → hardcoded
          client fallback (mirrors server/lib/tosManifest.js DOCS). */}
              <div
                className="settings-debug-section"
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <div style={{ flex: 1 }}>
                  <div className="settings-toggle-label">Replay ToS Gate</div>
                  <div className="settings-hint">
                    Re-show the terms-of-service window (normally only shown on first login)
                  </div>
                </div>
                <button
                  {...ui("settings.debug-replay-tos")}
                  type="button"
                  onClick={async () => {
                    let docs = Array.isArray(tos?.current) ? tos.current : null;
                    if (!docs || docs.length === 0) {
                      try {
                        const r = await fetchMe();
                        if (r.ok) {
                          const d = await r.json();
                          if (Array.isArray(d.tos?.current) && d.tos.current.length > 0)
                            docs = d.tos.current;
                        }
                      } catch (_e) {
                        /* fall through to client fallback */
                      }
                    }
                    if (!docs || docs.length === 0) {
                      // Client fallback mirrors server/lib/tosManifest.js DOCS so the
                      // preview shows the real legal docs even when the gate is inactive
                      // server-side (local dev) and /api/me carries no tos block.
                      docs = [
                        {
                          name: "terms",
                          displayName: "Terms of Service",
                          version: "2026-11-03",
                          url: "/legal/terms-2026-11-03.html",
                        },
                        {
                          name: "privacy",
                          displayName: "Privacy Policy",
                          version: "2026-11-03",
                          url: "/legal/privacy-2026-11-03.html",
                        },
                      ];
                    }
                    setTosPreview({ current: docs, accepted: {}, isCurrent: false });
                    setProOpen(false);
                  }}
                  style={{
                    background: "#33aa88",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "4px 12px",
                    fontFamily: '"IBM Plex Mono",monospace',
                    fontSize: 11,
                    fontWeight: "bold",
                    letterSpacing: 0,
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  SHOW
                </button>
              </div>
              {/* Voice Mode checkbox temporarily removed per request; state + behavior code below is intact. */}
              {voiceMode && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    userSelect: "none",
                    marginLeft: 28,
                  }}
                >
                  <div
                    {...ui("settings.debug-auto-read")}
                    onClick={() => setAutoRead((v) => !v)}
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      border: `2px solid ${autoRead ? (proColors ? C.purple : "#33aa88") : C.border}`,
                      background: autoRead ? (proColors ? C.purple : "#33aa88") : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    {autoRead && (
                      <span style={{ color: "#fff", fontSize: 11, fontWeight: "bold" }}>✓</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: C.grayL }}>Auto-read responses</div>
                </label>
              )}

              {/* LLM Prompt Box */}
              <div className="settings-debug-section">
                <div
                  style={{
                    fontSize: 11,
                    color: "#33aa88",
                    letterSpacing: 0.6,
                    marginBottom: 6,
                    fontWeight: "bold",
                  }}
                >
                  LLM PROMPT
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <div style={{ fontSize: 11, color: C.gray, flexShrink: 0 }}>MODEL</div>
                  <input
                    {...ui("settings.debug-llm-model")}
                    value={llmModel}
                    onChange={(e) => setLlmModel(e.target.value)}
                    placeholder="default (from config)"
                    style={{
                      flex: 1,
                      background: C.inputBg,
                      border: `1px solid ${C.border}`,
                      borderRadius: 4,
                      color: C.white,
                      fontFamily: '"IBM Plex Mono",monospace',
                      fontSize: 11,
                      padding: "3px 6px",
                      outline: "none",
                    }}
                  />
                </div>
                <textarea
                  {...ui("settings.debug-llm-prompt")}
                  className={proColors ? "pro-input" : ""}
                  value={llmPrompt}
                  onChange={(e) => setLlmPrompt(e.target.value)}
                  placeholder="Enter a custom prompt..."
                  style={{
                    width: "100%",
                    background: C.inputBg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.white,
                    fontFamily: '"IBM Plex Mono",monospace',
                    fontSize: 11,
                    padding: 6,
                    resize: "vertical",
                    height: 36,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                  <div
                    {...ui("settings.debug-llm-send")}
                    onClick={async () => {
                      if (llmLoading || !llmPrompt.trim()) return;
                      setLlmLoading(true);
                      setLlmResponse(null);
                      try {
                        const body = { prompt: llmPrompt };
                        if (llmModel.trim()) body.model = llmModel.trim();
                        const r = await postLlm(body);
                        const data = await r.json();
                        setLlmResponse({
                          ok: r.ok,
                          text: r.ok
                            ? data.response || JSON.stringify(data)
                            : data.error || data.detail || `Error ${r.status}`,
                          model: data.model || "",
                        });
                      } catch (e) {
                        setLlmResponse({
                          ok: false,
                          text: "Request failed: " + e.message,
                          model: "",
                        });
                      }
                      setLlmLoading(false);
                    }}
                    style={{
                      background: llmLoading ? "#333" : "#33aa88",
                      color: "#fff",
                      borderRadius: 6,
                      padding: "4px 12px",
                      fontFamily: '"IBM Plex Mono",monospace',
                      fontSize: 11,
                      fontWeight: "bold",
                      letterSpacing: 0,
                      cursor: llmLoading ? "wait" : "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {llmLoading ? "RUNNING..." : "SEND"}
                  </div>
                </div>
                {llmResponse && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "8px 10px",
                      background: C.srcBg,
                      borderRadius: 6,
                      border: `1px solid ${llmResponse.ok ? "#33aa88" : C.red}44`,
                      fontSize: 11,
                      fontFamily: '"IBM Plex Mono",monospace',
                      lineHeight: 1.5,
                      maxHeight: 80,
                      overflowY: "auto",
                    }}
                  >
                    {llmResponse.model && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#33aa88",
                          letterSpacing: 0,
                          marginBottom: 2,
                        }}
                      >
                        MODEL: {llmResponse.model}
                      </div>
                    )}
                    <div
                      style={{
                        color: llmResponse.ok ? C.grayL : C.red,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {llmResponse.text}
                    </div>
                  </div>
                )}
              </div>

              {/* Conditional Probabilities Table */}
              {active &&
                (active.events || []).some((e) => !e.isPast && e.p_conditional != null) && (
                  <div className="settings-debug-section">
                    <div
                      style={{
                        fontSize: 11,
                        color: "#33aa88",
                        letterSpacing: 0.6,
                        marginBottom: 6,
                        fontWeight: "bold",
                      }}
                    >
                      CONDITIONAL PROBABILITIES
                    </div>
                    <div
                      style={{
                        background: C.srcBg,
                        borderRadius: 6,
                        border: `1px solid ${C.border}`,
                        padding: 6,
                        maxHeight: 160,
                        overflowY: "auto",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 50px 50px 40px",
                          gap: "2px 6px",
                          fontSize: 11,
                          fontFamily: '"IBM Plex Mono",monospace',
                        }}
                      >
                        <div style={{ color: C.gray, letterSpacing: 0 }}>EVENT</div>
                        <div style={{ color: C.gray, letterSpacing: 0, textAlign: "right" }}>
                          P(Y)
                        </div>
                        <div style={{ color: C.gray, letterSpacing: 0, textAlign: "right" }}>
                          P(X|Y)
                        </div>
                        <div style={{ color: C.gray, letterSpacing: 0, textAlign: "center" }}>
                          DIR
                        </div>
                        {(active.events || [])
                          .filter((e) => !e.isPast && e.p_conditional != null)
                          .map((e) => (
                            <React.Fragment key={eventKey(e)}>
                              <div
                                style={{
                                  color: C.grayL,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={e.title}
                              >
                                {e.title}
                              </div>
                              <div style={{ color: C.white, textAlign: "right" }}>
                                {Math.round((e.p_event || 0) * 100)}%
                              </div>
                              <div style={{ color: C.white, textAlign: "right" }}>
                                {Math.round((e.p_conditional || 0) * 100)}%
                              </div>
                              <div
                                style={{
                                  color: e.direction === "yes" ? C.blueTeam : C.redTeam,
                                  textAlign: "center",
                                  fontWeight: "bold",
                                }}
                              >
                                {e.direction === "yes" ? "Y" : "N"}
                              </div>
                            </React.Fragment>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* GCP Forecast Server Connection — debug-only (behind the triple-click debug menu). */}
          {debugUI && (
            <div className="settings-debug-section">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: C.yellow,
                    letterSpacing: 0.6,
                    fontWeight: "bold",
                    flexShrink: 0,
                  }}
                >
                  GCP SERVER
                </div>
                {gcpResponse && (
                  <div
                    style={{
                      fontSize: 11,
                      color: gcpResponse.ok ? C.green : C.red,
                      letterSpacing: 0,
                    }}
                  >
                    {gcpResponse.ok ? "✓ CONNECTED" : "✕ FAILED"}
                  </div>
                )}
                <div style={{ flex: 1 }} />
                <div
                  {...ui("settings.debug-gcp-test")}
                  onClick={async () => {
                    if (gcpLoading) return;
                    setGcpLoading(true);
                    setGcpResponse(null);
                    try {
                      const r = await fetchHealth();
                      const data = await r.json();
                      if (Array.isArray(data.forecasters)) {
                        applyForecasters(FORECASTER_INFO, data.forecasters);
                        setOrchestrators(
                          data.forecasters
                            .filter((f) => !(f.tag && HIDDEN_TIERS.has(f.tag)))
                            .map((f) => f.name),
                        );
                      }
                      setGcpResponse({
                        ok: r.ok,
                        status: r.status,
                        text: JSON.stringify(data, null, 2),
                      });
                    } catch (e) {
                      setGcpResponse({
                        ok: false,
                        status: 0,
                        text: "Connection failed: " + e.message,
                      });
                    }
                    setGcpLoading(false);
                  }}
                  style={{
                    background: gcpLoading ? "#333" : C.purple,
                    color: "#fff",
                    borderRadius: 6,
                    padding: "3px 10px",
                    fontFamily: '"IBM Plex Mono",monospace',
                    fontSize: 11,
                    fontWeight: "bold",
                    letterSpacing: 0,
                    cursor: gcpLoading ? "wait" : "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {gcpLoading ? "..." : "TEST"}
                </div>
              </div>
              {gcpResponse && (
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 10px",
                    background: C.debugBg,
                    borderRadius: 6,
                    border: `1px solid ${gcpResponse.ok ? C.green : C.red}33`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: gcpResponse.ok ? C.green : C.red,
                      letterSpacing: 0,
                      marginBottom: 4,
                    }}
                  >
                    HTTP {gcpResponse.status || "ERR"}
                  </div>
                  <div
                    style={{
                      color: C.grayL,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      fontSize: 11,
                      fontFamily: '"IBM Plex Mono",monospace',
                    }}
                  >
                    {gcpResponse.text}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
